<template>
    <div>
        <div id="app-header" class="is-fluid">
            <mpn-header v-if="isAuthenticated" />
            <launch-header v-show="isAuthenticated" :authenticated-user="userDetails" />
        </div>
        <div id="main-portal-container">
            <!--authenticated user, render app-->
            <div v-show="isAuthenticated" id="" class="columns">
                <div id="launch-menu" class="column is-narrow-tablet">
                    <launch-menu />
                </div>
                <div id="launch-content" class="column is-9-desktop is-7-fullhd">
                    <!-- Dynamically changed content based on route match -->
                    <router-view :authenticated-user="userDetails" :engagements="salesforceEngagements" />
                </div>
            </div>
            <footer v-show="isAuthenticated" id="app-footer" role="contentinfo">
                Contact <a href="mailto:mpnsupport@mitre.org">the support team</a> for help and inquiries
            </footer>
        </div>
    </div>
</template>

<script>
import Api from './services/api';
import mpnHeader from './components/mpn-header.vue';
import launchHeader from './components/launchpad-header.vue';
import launchMenu from './components/launchpad-menu.vue';

export default {
    name: 'app',
    components: {
        mpnHeader,
        launchHeader,
        launchMenu,
    },
    data() {
        return {
            claims: {},
            isAuthenticated: null,
        };
    },
    computed: {
        userDetails() {
            console.log('APP update user from Okta claims', this.claims);
            let userDetails = {};
            if (Object.keys(this.claims).length) {
                userDetails = {
                    fullName: this.claims.name,
                    firstName: this.claims.given_name,
                    lastName: this.claims.family_name,
                    email: this.claims.email,
                };
            }
            return userDetails;
        },
        cleanPortalData() {
            console.log('APP clean portal data from Okta claims', this.claims);
            let userPortalData = [];
            let cleanedPortalData = [];
            if (Object.keys(this.claims).length) {
                userPortalData = (this.claims && this.claims.partnerProjectAssetDetails) ? this.claims.partnerProjectAssetDetails : [];
                // replace | with '
                if (userPortalData && userPortalData.length) {
                    cleanedPortalData = userPortalData.replace(/[|]/g, '"');
                    // remove escape characters
                    cleanedPortalData = cleanedPortalData.replace(/[\r\n]/g, '');
                    // parse data from string to JS object so we can use it
                    cleanedPortalData = JSON.parse(cleanedPortalData);
                }
            }
            return cleanedPortalData;
        },
        salesforceEngagements() {
            const portalEngagements = this.cleanPortalData?.Engagements?.length ? this.cleanPortalData?.Engagements : [];

            if (portalEngagements?.length) {
                // sort tools within projects and workspaces within tools alphabetically
                portalEngagements.forEach((engagement) => {
                    engagement.tools.sort((a, b) => ((a.name > b.name) ? 1 : -1));
                    engagement.tools.forEach((tool) => {
                        tool.workspace.sort((a, b) => ((a.title > b.title) ? 1 : -1));
                    });
                });
                // add a more descriptively named field until we can get the response changed
                // eslint-disable-next-line no-return-assign
                portalEngagements.map((engagement) => engagement.engagementId = engagement.number);
            }
            return portalEngagements;
        },
    },
    watch: {
        $route(from) {
            // TODO remove after validating
            // Track route changes in Piwik/Matomo https://developer.matomo.org/guides/spa-tracking
            if (!(window && window.location && window.location.pathname && window.location.pathname.match(/\/login/))) {
                console.log('APP WATCH route change NOT Okta Login redirect', window.location.pathname, from);
            }
        },
        authState: {
            immediate: true,
            async handler(authState) {
                if (authState?.isAuthenticated) {
                    Api.setupAxiosAuthInterceptor(this.$auth);
                    // user authenticated so get Okta claims for logged in user
                    this.claims = await this.$auth.getUser();
                    // TODO remove test data after fully implemented
                    // mimic a rich user config (projects only)
                    // this.claims = JSON.parse('{"sub":"00uffyg4mk8NWUBmS296","name":"Stan A Drozdetski","locale":"US","email":"drozdetski@mitre.org","preferred_username":"drozdetski@mitre.org","given_name":"Stan","family_name":"Drozdetski","zoneinfo":"America/Los_Angeles","updated_at":1644595984,"email_verified":true,"partnerProjectAssetDetails":"{\\n |projects| : [ {\\n |name| : |MITRE Partnership Network x Launch|,\\n |description| : ||,\\n |number| : |01AOH124-MP|,\\n |shareDeliverables| : false,\\n |tools| : [ { |name| : |Box|, |helpUrl| : |http://box.com/|, |workspace| : [ {|title|:|MITRE Partnership Network x Launch - Stan|,|workspaceId|:|145825824029|,|url|:|match|}, {|title|:|MPN-General|,|workspaceId|:|143747842696|,|url|:|match|}, {|title|:|MITRE Partnership Network x Launch - Stan 2|,|workspaceId|:|145826758854|,|url|:|match|}, {|title|:|MITRE Partnership Network x Launch Oct 20|,|workspaceId|:|148437821113|,|url|:|match|}, {|title|:|MITRE Partnership Network x Launch - Oct 21-3|,|workspaceId|:|148494252757|,|url|:|match|}, {|title|:|MITRE Partnership Network x Launch - CaaP Demo|,|workspaceId|:|146320582740|,|url|:|match|} ]}, { |name| : |Slack|, |helpUrl| : |https://slack.com/|, |workspace| : [ {|title|:|MPN-General|,|workspaceId|:|C02C08A857T|,|url|:|slackURL|}, {|title|:|Proj-MITRE-Partnership-Network-x-Launch|,|workspaceId|:|C02FFQX96HG|,|url|:|slackURL|}, {|title|:|MPN-Dev|,|workspaceId|:|C02BRE394QJ|,|url|:|slackURL|} ]} ]\\n }, {\\n |name| : |MITRE Engineering Platform|,\\n |description| : ||,\\n |number| : |01AOHOPX-SC|,\\n |shareDeliverables| : false,\\n |tools| : [ { |name| : |Website|, |helpUrl| : |Null|, |workspace| : [ {|title|:|MEP Restricted Website|,|workspaceId|:|null|,|url|:|https://ep.mitre.org/|} ]} ]\\n }, {\\n |name| : |Great Power Competition|,\\n |description| : ||,\\n |number| : |GNA001.A998.SPI.GP|,\\n |shareDeliverables| : false,\\n |tools| : [ { |name| : |Website|, |helpUrl| : |Null|, |workspace| : [ {|title|:|Test Serious Games Instance|,|workspaceId|:|null|,|url|:|https://www.mitre.org|} ]} ]\\n }, {\\n |name| : |MiDAS|,\\n |description| : ||,\\n |number| : |OVH001.ECIS.SMD.OS|,\\n |shareDeliverables| : false,\\n |tools| : [ { |name| : |Website|, |helpUrl| : |Null|, |workspace| : [ {|title|:|MiDAS Data Service|,|workspaceId|:|null|,|url|:|https://sme.midas-prod.mitre.org|} ]} ]\\n }, {\\n |name| : |Testing and Diagnostic Working Group|,\\n |description| : ||,\\n |number| : |37218206-PM|,\\n |shareDeliverables| : false,\\n |tools| : [ { |name| : |Website|, |helpUrl| : |Null|, |workspace| : [ {|title|:|TDWG Collaboration Space|,|workspaceId|:|null|,|url|:|https://teams.microsoft.com/l/team/19%3a3wmcvOknOErTNP_8fTCM8JQuj3S7g_pRH-dubdYWr601%40thread.tacv2/conversations?groupId=b85ea494-890f-4891-9e66-67a7bf440376&tenantId=92c280dd-8c0e-4c98-bd6c-622dacc5689b|}, {|title|:|TDWG Collab Portal (MiDAS)|,|workspaceId|:|null|,|url|:|https://collab.tdwg.mitre.org|} ]} ]\\n } ]\\n}","uid":"drozdetski","employeenumber":"31044"}');
                    console.log('APP WATCH authenticated - claims', this.claims);
                    this.isAuthenticated = true;
                } else {
                    console.log('APP WATCH not authenticated', authState);
                    this.isAuthenticated = false;
                }
            },
            deep: true,
        },
    },
};
</script>

export default App;

<style lang="scss">
@import "./styles/constants.scss";
#app-header {
    background-color: $mitreDarkNavy;
    border-bottom: 3px solid $mitreHighlighterYellow;
}
#main-portal-container {
    padding-top: .7em;
    background-color: $white;
    .column.is-narrow-tablet {
        width: 100%;
    }
}
#launch-menu.column {
    padding-right: 0;
    padding-bottom: 0;
    background-color: $mitreSilver;
}
#launch-content.column {
    padding-left: 0;
    padding-top: 0;
}
#app-footer {
    text-align: center;
    padding: 1rem 0rem 1rem 0rem;
    background-color: $mitreLightSilver;
    margin-top: -0.75rem;
}

@include tablet {
    #main-portal-container {
        .column.is-narrow-tablet {
            width: 6em;
        }
    }
}
</style>
